import React from 'react'
import { Button, DropdownItemProps } from 'semantic-ui-react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { IRejection, IWarning } from '@cbgms/api'
import RejectionDialog from './RejectionDialog'
import { IRejectionMessage } from './RejectionMessage'

export interface MappingOptions {
  isError: boolean
  isPortal: boolean
}

export interface DeleteResponseToMessageMapper {
  (apiResponse: IRejection[] | IWarning[], item: string, options?: MappingOptions): IRejectionMessage[]
}

export interface IDeleteModalContainerProps {
  item: string
  showModal: boolean
  closeModal: () => void
  handleRemove: (dryRun: boolean, reason?: string) => any
  skipDryRun?: boolean
  deleteReasons?: DropdownItemProps[]
  skipCloseModal?: boolean
  showMultiple?: boolean
  isPortal?: boolean
  mapApiResponseToMessage: DeleteResponseToMessageMapper
}

export interface IDeleteModalContainerState {
  rejections: IRejectionMessage[]
  warnings: IRejectionMessage[]
  reason: string
  submitting: boolean
}

export interface DeleteModalContainerProps extends IDeleteModalContainerProps, WithTranslation {}

/**
 * DeleteModalContainer
 * @deprecated prefer to use a components/delete-flow
 *  */
export class DeleteModalContainer extends React.PureComponent<DeleteModalContainerProps, IDeleteModalContainerState> {
  state: IDeleteModalContainerState = {
    rejections: [],
    warnings: [],
    reason: '',
    submitting: false
  }

  handleRealDelete = async () => {
    const { handleRemove, closeModal, skipCloseModal, deleteReasons } = this.props
    const { reason } = this.state

    this.setState(() => ({
      submitting: true
    }))

    const reasons = deleteReasons && deleteReasons.length ? reason : undefined

    try {
      await handleRemove(false, reasons)
    } catch {
      return
    } finally {
      this.setState(() => ({
        submitting: true
      }))
    }

    if (!skipCloseModal) {
      closeModal()
    }
  }

  async componentDidMount() {
    if (this.props.skipDryRun) {
      return
    }

    const { handleRemove, mapApiResponseToMessage, item, isPortal = false } = this.props

    let dryRunResults = null
    try {
      dryRunResults = await handleRemove(true)
    } catch (e: any) {
      this.setState({
        rejections: e.errors && e.errors.rejections ? mapApiResponseToMessage(e.errors.rejections, item, { isError: true, isPortal }) : [],
        warnings: e.errors && e.errors.warnings ? mapApiResponseToMessage(e.errors.warnings, item, { isError: false, isPortal }) : []
      })

      return
    }

    if (dryRunResults && dryRunResults.Data && dryRunResults.Data.Rejections && dryRunResults.Data.Rejections.length > 0) {
      this.setState({
        rejections: mapApiResponseToMessage(dryRunResults.Data.Rejections, item, { isError: true, isPortal })
      })
    }

    if (dryRunResults && dryRunResults.Data && dryRunResults.Data.Warnings && dryRunResults.Data.Warnings.length > 0) {
      this.setState({
        warnings: mapApiResponseToMessage(dryRunResults.Data.Warnings, item, { isError: false, isPortal })
      })
    }
  }

  updateReason = (reason: string) => this.setState(() => ({ reason }))

  render() {
    const { showModal, closeModal, item, deleteReasons, t, showMultiple } = this.props
    const { rejections, warnings, reason, submitting } = this.state
    const disabledConfirmButton = submitting || rejections.length > 0 || (deleteReasons && deleteReasons.length > 0 && !reason)
    return (
      <RejectionDialog
        item={item}
        rejections={rejections}
        warnings={warnings}
        open={showModal}
        reasons={deleteReasons}
        onReasonChange={this.updateReason}
        showMultiple={showMultiple}
        onLinkClick={closeModal}
        title={t('DeleteModal.BaseTitle', { item })}
        description={t('DeleteModal.BaseDescription', { item })}
        renderActions={
          <React.Fragment>
            <Button
              data-testid='deletemodal-cancel-button'
              color='orange'
              content={t('DeleteModal.Cancel')}
              icon='cancel'
              onClick={closeModal}
            />
            <Button
              data-testid='deletemodal-submit-button'
              type='submit'
              content={t('global.delete')}
              disabled={disabledConfirmButton}
              icon='trash'
              negative
              autoFocus
              onClick={this.handleRealDelete}
            />
          </React.Fragment>
        }
      />
    )
  }
}

/**
 * @deprecated prefer to use components/delete-flow
 */
export default withTranslation()(DeleteModalContainer)
